import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { CsvAsStringArrays } from '@malou-io/package-utils';

import {
    ParentTopicMentionSummaryCsvData,
    ParentTopicPriority,
} from '#modules/statistics-e-reputation/components/aggregated-e-reputation/semantic-analysis-v2/models/parent-topic-mention-summary';
import { AggregatedSemanticAnalysisExportService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-semantic-analysis-export.service';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { SentenceCasePipe } from '#shared/pipes/sentence-case.pipe';

type Data = ParentTopicMentionSummaryCsvData[] | null;
@Injectable()
export class AggregatedSemanticAnalysisParentTopicSummaryInsightsService extends AbstractCsvService<Data> {
    private readonly _sentenceCasePipe = new SentenceCasePipe();
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);
    private readonly _translateService = inject(TranslateService);
    private readonly _aggregatedSemanticAnalysisExportService = inject(AggregatedSemanticAnalysisExportService);

    private readonly _parentTopicPriorityLabelMap: Record<ParentTopicPriority, string> = {
        [ParentTopicPriority.URGENT]: this._translateService.instant(
            'aggregated_statistics.e_reputation.parent_topic_mention_summary.priority_labels.urgent'
        ),
        [ParentTopicPriority.WORRYING]: this._translateService.instant(
            'aggregated_statistics.e_reputation.parent_topic_mention_summary.priority_labels.worrying'
        ),
        [ParentTopicPriority.MODERATE]: this._translateService.instant(
            'aggregated_statistics.e_reputation.parent_topic_mention_summary.priority_labels.moderate'
        ),
        [ParentTopicPriority.HEALTHY]: this._translateService.instant(
            'aggregated_statistics.e_reputation.parent_topic_mention_summary.priority_labels.healthy'
        ),
    };

    private readonly _parentTopicMentionSummaryData$ = toObservable(
        this._aggregatedStatisticsStore.semanticAnalysisV2ParentTopicMentionSummaryData
    );

    protected _getData$(): Observable<Data> {
        return this._aggregatedSemanticAnalysisExportService.waitForData$(this._parentTopicMentionSummaryData$);
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return ['Topic', 'Grouped', 'Negative Mentions', 'Positive Mentions', '# Locations', 'Priority'];
    }

    protected override _getCsvDataRows(data: ParentTopicMentionSummaryCsvData[]): CsvAsStringArrays {
        return data.map((item) => {
            const { positiveMentions, negativeMentions, restaurantsCount, priority, isGroupParent } = item;
            const priorityLabel = this._parentTopicPriorityLabelMap[priority] ?? '-';
            const displayedTopicName = this._sentenceCasePipe.transform(item.displayedTopicName) ?? item.displayedTopicName;
            return [
                displayedTopicName,
                isGroupParent ? 'Yes' : 'No',
                negativeMentions.toString(),
                positiveMentions.toString(),
                restaurantsCount.toString(),
                priorityLabel,
            ];
        });
    }
}
